import { useState } from "react";
import StatusPill from "@/components/StatusPill";
import UrgentPill from "@/components/UrgentPill";
import { AvatarStack, UserAvatar } from "@/components/UserAvatar";
import { formatDate, isOverdue, isToday } from "@/lib/tp";
import { isHiddenUser } from "@/lib/roles";
import { useData } from "@/context/DataContext";
import { FileText, Pencil, CalendarDays, Repeat, Check, UserPlus, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import TaskModal from "@/components/TaskModal";
import IstruzioniDrawer from "@/components/IstruzioniDrawer";
import ConfettiBurst from "@/components/ConfettiBurst";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function toDateFromISO(s) {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function toISOFromDate(d) {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function TaskRow({ task, showClient = false, inTrash = false }) {
  const { users, clients, updateTask, addTask } = useData();
  const [editOpen, setEditOpen] = useState(false);
  const [istruzioniOpen, setIstruzioniOpen] = useState(false);
  const [dupCalOpen, setDupCalOpen] = useState(false);
  const [confetti, setConfetti] = useState(0);

  const assignableUsers = users.filter(
    (u) => !isHiddenUser(u),
  );

  const assignees = users.filter((u) => task.assignee_ids?.includes(u.id));
  const client = clients.find((c) => c.id === task.client_id);

  const overdue = (isOverdue(task.due_date) || task.was_overdue) && task.status !== "completato";
  const today = isToday(task.due_date);
  const urgent = task.priority === 5;
  const highPriority = urgent && task.status !== "completato";

  const hasIstruzioni = !!(task.description && task.description.trim().length > 0);
  const checklist = Array.isArray(task.checklist) ? task.checklist : [];
  const checklistDone = checklist.filter((c) => c.done).length;
  const hasChecklist = checklist.length > 0;
  const istruzioniLabel = hasChecklist
    ? `${checklistDone}/${checklist.length}`
    : hasIstruzioni
      ? "1"
      : "0";
  const iconActive = hasIstruzioni || hasChecklist;

  const completed = task.status === "completato";
  let highlightCls = "";
  if (overdue) {
    highlightCls =
      "shadow-[inset_0_0_0_2px_rgba(239,68,68,0.6),0_0_18px_-6px_rgba(239,68,68,0.55)] bg-red-500/[0.06]";
  } else if (highPriority) {
    highlightCls =
      "shadow-[inset_0_0_0_2px_rgba(245,158,11,0.6),0_0_18px_-6px_rgba(245,158,11,0.55)] bg-amber-500/[0.06]";
  }
  const completedCls = completed ? "opacity-55 bg-black/20" : "";

  const onStatus = async (s) => {
    if (s === "completato" && task.status !== "completato") {
      setConfetti((c) => c + 1);
    }
    await updateTask(task.id, { status: s });
    toast.success("Stato aggiornato");
    if (s === "completato" && task.duplicate_on_complete) {
      setDupCalOpen(true);
    }
  };
  const onUrgent = async (v) => {
    await updateTask(task.id, { priority: v ? 5 : 3 });
  };
  const onToggleDuplicate = async () => {
    await updateTask(task.id, { duplicate_on_complete: !task.duplicate_on_complete });
    toast.success(
      task.duplicate_on_complete ? "Duplicazione disattivata" : "Duplicazione attiva: al completamento sceglierai la data",
    );
  };
  const onDuplicateDate = async (d) => {
    if (!d) return;
    const due = toISOFromDate(d);
    await addTask({
      title: task.title,
      description: task.description || "",
      client_id: task.client_id,
      assignee_ids: task.assignee_ids || [],
      due_date: due,
      priority: task.priority,
      status: "da_fare",
      notify_user_id: task.notify_user_id || null,
      checklist: (Array.isArray(task.checklist) ? task.checklist : []).map((c) => ({
        ...c,
        done: false,
      })),
      attachments: task.attachments || [],
      duplicate_on_complete: true,
    });
    setDupCalOpen(false);
    toast.success(`Task duplicata per il ${formatDate(due)}`);
  };
  const onDate = async (d) => {
    await updateTask(task.id, { due_date: toISOFromDate(d) });
    toast.success("Scadenza aggiornata");
  };
  const onToggleAssignee = async (uid) => {
    const set = new Set(task.assignee_ids || []);
    if (set.has(uid)) set.delete(uid);
    else set.add(uid);
    await updateTask(task.id, { assignee_ids: [...set] });
  };

  return (
    <>
      <div
        data-testid={`task-card-${task.id}`}
        data-overdue={overdue ? "true" : "false"}
        data-high-priority={highPriority ? "true" : "false"}
        className={`grid grid-cols-[1fr_auto] sm:grid-cols-[minmax(0,1fr)_140px_130px_130px_120px_auto] gap-3 items-center px-3 sm:px-4 py-3 rounded-xl transition-colors border-b border-white/5 last:border-0 my-1 ${completedCls} ${highlightCls || "hover:bg-white/[0.04]"}`}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            {showClient && client && (
              <div
                data-testid={`task-client-dot-${task.id}`}
                className="h-6 w-6 rounded-full overflow-hidden shrink-0 flex items-center justify-center text-white text-[9px] font-bold"
                style={{
                  backgroundColor: client.avatar_url ? "transparent" : (client.color || "#0A84FF"),
                  backgroundImage: client.avatar_url ? `url(${client.avatar_url})` : undefined,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
                title={client.name}
              >
                {!client.avatar_url && (client.name || "?").slice(0, 2).toUpperCase()}
              </div>
            )}
            <button
              onClick={() => setEditOpen(true)}
              className="text-sm font-medium text-white text-left truncate hover:underline uppercase tracking-wider"
            >
              {task.title}
            </button>
            {task.recurring && (
              <span
                data-testid={`task-recurring-badge-${task.id}`}
                title={`Task ricorrente ogni ${task.recurring_days || 7} giorni`}
                className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest tp-brand-gradient text-white shadow shrink-0"
              >
                <Repeat size={9} />
                {task.recurring_days || 7}g
              </span>
            )}
          </div>
          {task.description && (
            <div className="text-xs text-zinc-500 truncate mt-0.5">{task.description}</div>
          )}
          {showClient && client && (
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{client.name}</div>
          )}
        </div>

        {/* Team - clickable to edit assignees */}
        <div className="hidden sm:block">
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid={`task-team-button-${task.id}`}
                className="inline-flex items-center gap-1 rounded-lg px-1.5 py-1 hover:bg-white/5 transition-colors"
              >
                {assignees.length > 0 ? (
                  <AvatarStack users={assignees} />
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300">
                    <UserPlus size={13} /> Assegna
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="tp-glass-strong border-white/10 text-white w-64 p-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 px-1 pb-1">
                Assegnatari
              </div>
              <div className="max-h-64 overflow-y-auto">
                {assignableUsers.length === 0 && (
                  <div className="text-xs text-zinc-500 p-2">Nessun utente disponibile.</div>
                )}
                {assignableUsers.map((u) => {
                  const active = task.assignee_ids?.includes(u.id);
                  return (
                    <button
                      key={u.id}
                      onClick={() => onToggleAssignee(u.id)}
                      data-testid={`row-assignee-toggle-${task.id}-${u.id}`}
                      className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-white/10 transition-colors ${active ? "bg-white/5" : ""}`}
                    >
                      <UserAvatar user={u} size={22} ring={false} />
                      <span className="flex-1 truncate">{u.name}</span>
                      {active && <Check size={14} className="text-fuchsia-300" />}
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Date - clickable to edit */}
        <div className="hidden sm:block">
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid={`task-date-button-${task.id}`}
                className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs hover:bg-white/5 transition-colors"
              >
                <CalendarDays size={13} className="text-zinc-500" />
                <span
                  className={
                    overdue
                      ? "text-red-400 font-medium"
                      : today
                        ? "text-amber-300 font-medium"
                        : "text-zinc-300"
                  }
                >
                  {formatDate(task.due_date)}
                </span>
                {today && (
                  <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-amber-400 tp-pulse-dot" />
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="tp-glass-strong border-white/10 p-0 text-white w-auto" align="start">
              <Calendar
                mode="single"
                weekStartsOn={1}
                disabled={(d) => { const w = d.getDay(); return w === 0 || w === 6; }}
                selected={toDateFromISO(task.due_date)}
                onSelect={onDate}
                initialFocus
              />
              {task.due_date && (
                <div className="p-2 border-t border-white/10">
                  <button
                    onClick={() => onDate(null)}
                    className="w-full text-xs text-zinc-400 hover:text-white py-1.5"
                  >
                    Rimuovi scadenza
                  </button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>

        {/* Urgente */}
        <div className="hidden sm:block">
          <UrgentPill urgent={urgent} onChange={onUrgent} testId={`task-urgent-toggle-${task.id}`} />
        </div>

        {/* Status */}
        <div className="hidden sm:block relative">
          <StatusPill status={task.status} onChange={onStatus} testId={`task-status-pill-${task.id}`} />
          <ConfettiBurst fire={confetti} />
        </div>

        {/* Actions */}
        {!inTrash && (
        <div className="flex items-center gap-1 justify-end">
          <button
            data-testid={`task-duplicate-toggle-${task.id}`}
            onClick={onToggleDuplicate}
            aria-pressed={task.duplicate_on_complete ? "true" : "false"}
            title={
              task.duplicate_on_complete
                ? "Duplicazione attiva — al completamento scegli la data"
                : "Attiva duplicazione al completamento"
            }
            className={`p-2 rounded-lg transition-colors ${
              task.duplicate_on_complete
                ? "tp-brand-btn text-white"
                : "text-zinc-500 hover:text-fuchsia-300 hover:bg-white/5"
            }`}
          >
            <Copy size={15} />
          </button>
          <button
            data-testid={`task-istruzioni-button-${task.id}`}
            onClick={() => setIstruzioniOpen(true)}
            className="relative p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-colors inline-flex items-center gap-1"
            aria-label="Istruzioni"
            title="Istruzioni"
          >
            <FileText size={15} className={iconActive ? "text-fuchsia-300" : ""} />
            <span
              data-testid={`task-istruzioni-count-${task.id}`}
              className={`text-[11px] font-semibold tabular-nums ${
                iconActive ? "text-white" : "text-zinc-500"
              }`}
            >
              {istruzioniLabel}
            </span>
          </button>
          <button
            onClick={() => setEditOpen(true)}
            className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
            aria-label="Modifica"
            data-testid={`task-edit-button-${task.id}`}
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={async (e) => {
              e.stopPropagation();
              if (task.trashed) return;
              if (!confirm("Spostare questa task nel Cestino?")) return;
              await updateTask(task.id, { trashed: true });
              toast.success("Task spostata nel cestino");
            }}
            className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            aria-label="Sposta nel cestino"
            title="Sposta nel cestino"
            data-testid={`task-trash-button-${task.id}`}
          >
            <Trash2 size={14} />
          </button>
        </div>
        )}

        {/* Mobile compact row */}
        <div className="sm:hidden col-span-2 flex items-center gap-2 mt-1 flex-wrap">
          <span className="relative inline-flex">
            <StatusPill status={task.status} onChange={onStatus} />
            <ConfettiBurst fire={confetti} />
          </span>
          <UrgentPill urgent={urgent} onChange={onUrgent} />
          <div className="text-[11px] text-zinc-400 ml-auto">{formatDate(task.due_date)}</div>
          <AvatarStack users={assignees} size={22} />
        </div>
      </div>

      <TaskModal open={editOpen} onOpenChange={setEditOpen} editingTask={task} />
      <IstruzioniDrawer open={istruzioniOpen} onOpenChange={setIstruzioniOpen} task={task} />

      <Dialog open={dupCalOpen} onOpenChange={setDupCalOpen}>
        <DialogContent className="tp-glass-strong border-white/10 text-white w-auto max-w-fit" data-testid={`task-duplicate-calendar-${task.id}`}>
          <DialogHeader>
            <DialogTitle className="text-white text-base">
              Duplica "{task.title}" — scegli la data
            </DialogTitle>
          </DialogHeader>
          <Calendar
            mode="single"
            weekStartsOn={1}
            disabled={(d) => { const w = d.getDay(); return w === 0 || w === 6; }}
            onSelect={onDuplicateDate}
            initialFocus
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
